import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ymdToLocalDate } from '../../utils/flightDateUtils';
import FareDateCell from './FareDateCell';
import {
  applyDatePick,
  buildFareDateGrid,
  deferAfterPaint,
  publishFareSelection,
  selectionRole,
  toIsoDate,
  usesReturnFare,
} from './fareDateGrid';

function emptySelection(start, end, tripType) {
  return {
    start: toIsoDate(start) || null,
    end: tripType === 'roundtrip' ? (toIsoDate(end) || null) : null,
  };
}

/**
 * Fare calendar for the mobile Search Date sheet.
 * The tapped day is highlighted in this component. The sheet footer updates after paint.
 */
const FareDateCalendar = React.memo(function FareDateCalendar({
  monthCount,
  minDate,
  maxDate,
  tripType,
  departurePrices,
  returnPrices,
  initialStart,
  initialEnd,
  externalStart,
  externalEnd,
  onSelectionChange,
  publishedSelectionRef,
}) {
  const [selection, setSelection] = useState(() => emptySelection(initialStart, initialEnd, tripType));
  const selectionRef = useRef(selection);
  const tripTypeRef = useRef(tripType);
  const onChangeRef = useRef(onSelectionChange);
  const publishedRef = useRef(publishedSelectionRef);
  const cancelNotifyRef = useRef(() => {});
  selectionRef.current = selection;
  tripTypeRef.current = tripType;
  onChangeRef.current = onSelectionChange;
  publishedRef.current = publishedSelectionRef;
  publishFareSelection(publishedSelectionRef, selection);

  const minKey = toIsoDate(minDate);
  const maxKey = toIsoDate(maxDate);
  const grid = useMemo(
    () => buildFareDateGrid({
      monthCount,
      minKey,
      maxKey,
      departurePrices,
      returnPrices,
    }),
    [monthCount, minKey, maxKey, departurePrices, returnPrices],
  );

  useEffect(() => {
    const start = toIsoDate(externalStart);
    const end = toIsoDate(externalEnd);
    setSelection((prev) => {
      const next = {
        start: start ? prev.start : null,
        end: end ? prev.end : null,
      };
      if (next.start === prev.start && next.end === prev.end) return prev;
      cancelNotifyRef.current();
      selectionRef.current = next;
      publishFareSelection(publishedRef.current, next);
      return next;
    });
  }, [externalStart, externalEnd]);

  useEffect(() => () => cancelNotifyRef.current(), []);

  const onPick = useCallback((iso) => {
    const next = applyDatePick(selectionRef.current, iso, tripTypeRef.current);
    selectionRef.current = next;
    publishFareSelection(publishedRef.current, next);
    setSelection(next);
    cancelNotifyRef.current();
    cancelNotifyRef.current = deferAfterPaint(() => {
      if (typeof onChangeRef.current === 'function') {
        onChangeRef.current(ymdToLocalDate(next.start), ymdToLocalDate(next.end));
      }
    });
  }, []);

  if (!grid) return null;

  return (
    <div className="fare-date-calendar" data-testid="fare-date-calendar">
      {grid.months.map((month) => (
        <section key={month.id} className="fare-date-month" aria-label={month.title}>
          <h3 className="fare-date-month-title">{month.title}</h3>
          <div className="fare-date-grid">
            {month.cells.map((cell, index) => {
              if (!cell) {
                return <span key={`${month.id}-pad-${index}`} className="fare-date-pad" aria-hidden="true" />;
              }
              const returnFare = usesReturnFare(cell.iso, selection.start, tripType);
              return (
                <FareDateCell
                  key={cell.iso}
                  iso={cell.iso}
                  day={cell.day}
                  disabled={cell.disabled}
                  priceLabel={returnFare ? cell.returnLabel : cell.departureLabel}
                  priceColor={returnFare ? cell.returnColor : cell.departureColor}
                  role={selectionRole(cell.iso, selection, tripType)}
                  onPick={onPick}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
});

export default FareDateCalendar;
